/**
 * The button (§7, §36, §37).
 *
 * Sizing is the part that matters. §7 asks for 44x44 minimum and prefers
 * 48x48+ for children; §37 asks for "one primary action per screen". So the
 * `primary` size is 56px tall and full-width by default — on a phone it is
 * unmissable, which is the point. A seven-year-old should not have to hunt.
 *
 * `busy` exists because §55 requires mutation buttons to be disabled while in
 * flight. It keeps the button's width stable while swapping in a spinner, so
 * the layout does not jump under the child's finger mid-tap.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  busy?: boolean;
  busyLabel?: string;
  fullWidth?: boolean;
  iconLeft?: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-[var(--color-primary)] text-[var(--color-text-inverse)] shadow-[var(--shadow-sm)] ' +
    'hover:bg-[var(--color-primary-hover)] active:translate-y-px',
  secondary:
    'bg-[var(--color-surface)] text-[var(--color-text)] border border-[var(--color-border)] ' +
    'hover:bg-[var(--color-surface-sunken)] active:translate-y-px',
  ghost: 'bg-transparent text-[var(--color-primary)] hover:bg-[var(--color-primary-soft)]',
  danger:
    'bg-[var(--color-danger-soft)] text-[var(--color-danger)] border border-[var(--color-danger)]/30 ' +
    'hover:bg-[var(--color-danger)] hover:text-white',
};

const SIZES: Record<Size, string> = {
  // Every size clears the 44px floor from §7.
  sm: 'min-h-[var(--tap-min)] px-4 text-[length:var(--text-sm)] rounded-[var(--radius-md)]',
  md: 'min-h-[var(--tap)] px-5 text-[length:var(--text-base)] rounded-[var(--radius-lg)]',
  lg: 'min-h-[var(--tap-primary)] px-6 text-[length:var(--text-lg)] rounded-[var(--radius-xl)]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    busy = false,
    busyLabel,
    fullWidth = false,
    iconLeft,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      // §55: "Disable mutation buttons while in flight."
      disabled={disabled || busy}
      // aria-busy tells a screen reader the control is working; `disabled`
      // alone would just read as unavailable, with no explanation.
      aria-busy={busy || undefined}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-[var(--weight-semibold)]',
        'transition-colors duration-[var(--duration-fast)]',
        'disabled:opacity-55 disabled:cursor-not-allowed disabled:active:translate-y-0',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {busy ? (
        <>
          <Spinner />
          <span>{busyLabel ?? children}</span>
        </>
      ) : (
        <>
          {iconLeft}
          {children}
        </>
      )}
    </button>
  );
});

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
